<template>
  <div>
    <!-- 사원 정보 업로드 모달 -->
    <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="900px" content-class="member-upload-dialog-wrapper">
      <v-card class="member-upload-card">
        <!-- 로딩 컴포넌트 -->
        <LodingComponent :is-visible="isUploadingMember" :text="loadingText" />
        
        <v-card-title class="member-upload-header">
          <v-icon color="white" class="mr-2">mdi-account-group</v-icon>
          사원 정보 업로드
        </v-card-title>
        
        <v-card-text class="member-upload-content">
          <!-- 데이터가 없을 때 파일 입력만 표시 -->
          <div v-if="memberTableData.length === 0">
            <v-file-input
              v-model="memberFile"
              accept=".xls,.xlsx"
              label="엑셀 파일을 선택하세요"
              variant="outlined"
              prepend-icon="mdi-file-excel"
              density="compact"
              hide-details
              @update:model-value="onMemberFileSelect"
            ></v-file-input>
          </div>
          
          <!-- 데이터가 있을 때 테이블 표시 -->
          <div v-if="memberTableData.length > 0" class="member-table-container">
            <div class="table-header">
              <h3>엑셀업로드로 등록된 사원: {{ filteredMemberData.length }}명 
                <p v-if="memberTableData[0]?.uploadDate">
                  마지막업로드: {{ formatUploadDate(memberTableData[0].uploadDate) }}
                </p>
              </h3>
              <div class="file-upload-area">
                <v-file-input
                  v-model="memberFile"
                  accept=".xls,.xlsx"
                  label="엑셀 업로드"
                  variant="outlined"
                  prepend-icon="mdi-file-excel"
                  density="compact"
                  hide-details
                  @update:model-value="onMemberFileSelect"
                ></v-file-input>
              </div>
            </div>
            
            <div v-if="memberFile" class="file-preview">
              <v-chip color="#1e3a8a" variant="tonal" size="small">
                <v-icon start>mdi-check-circle</v-icon>
                {{ memberFile.name }} ({{ formatFileSize(memberFile.size) }})
              </v-chip>
            </div>
            
            <!-- 검색 필터 -->
            <div class="member-search-filter">
              <v-text-field
                v-model="memberSearchFilter"
                @input="filterMemberData"
                label="성명 검색"
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-account-search"
                clearable
                class="member-search-input"
                hide-details
                width="150px"
              ></v-text-field>
              <v-checkbox
                v-model="excludeResigned"
                @change="filterMemberData"
                label="퇴사자 제외"
                density="compact"
                hide-details
                class="exclude-resigned-checkbox"
              ></v-checkbox>
            </div>
            
            <div class="table-wrapper">
              <v-table class="member-table" fixed-header>
                <thead>
                  <tr>
                    <th 
                      class="sortable-header"
                      @click="sortMemberData('name')"
                    >
                      <div class="header-content">
                        <span>성명</span>
                        <v-icon 
                          v-if="memberSortState.column === 'name'"
                          size="small"
                          class="sort-icon"
                        >
                          {{ memberSortState.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                        </v-icon>
                        <v-icon 
                          v-else
                          size="small"
                          class="sort-icon inactive"
                        >
                          mdi-unfold-more-horizontal
                        </v-icon>
                      </div>
                    </th>
                    <th 
                      class="sortable-header"
                      @click="sortMemberData('birthDate')"
                    >
                      <div class="header-content">
                        <span>생년월일</span>
                        <v-icon 
                          v-if="memberSortState.column === 'birthDate'"
                          size="small"
                          class="sort-icon"
                        >
                          {{ memberSortState.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                        </v-icon>
                        <v-icon 
                          v-else
                          size="small"
                          class="sort-icon inactive"
                        >
                          mdi-unfold-more-horizontal
                        </v-icon>
                      </div>
                    </th>
                    <th 
                      class="sortable-header"
                      @click="sortMemberData('gender')"
                    >
                      <div class="header-content">
                        <span>성별</span>
                        <v-icon 
                          v-if="memberSortState.column === 'gender'"
                          size="small"
                          class="sort-icon"
                        >
                          {{ memberSortState.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                        </v-icon>
                        <v-icon 
                          v-else
                          size="small"
                          class="sort-icon inactive"
                        >
                          mdi-unfold-more-horizontal
                        </v-icon>
                      </div>
                    </th>
                    <th 
                      class="sortable-header"
                      @click="sortMemberData('qualification')"
                    >
                      <div class="header-content">
                        <span>보유자격증</span>
                        <v-icon 
                          v-if="memberSortState.column === 'qualification'"
                          size="small"
                          class="sort-icon"
                        >
                          {{ memberSortState.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                        </v-icon>
                        <v-icon 
                          v-else
                          size="small"
                          class="sort-icon inactive"
                        >
                          mdi-unfold-more-horizontal
                        </v-icon>
                      </div>
                    </th>
                    <th 
                      class="sortable-header"
                      @click="sortMemberData('workPeriod')"
                    >
                      <div class="header-content">
                        <span>입사일</span>
                        <v-icon 
                          v-if="memberSortState.column === 'workPeriod'"
                          size="small"
                          class="sort-icon"
                        >
                          {{ memberSortState.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                        </v-icon>
                        <v-icon 
                          v-else
                          size="small"
                          class="sort-icon inactive"
                        >
                          mdi-unfold-more-horizontal
                        </v-icon>
                      </div>
                    </th>
                    <th>
                      <span>퇴사일</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(member, index) in filteredMemberData" :key="index">
                    <td>{{ member.name }}</td>
                    <td>{{ member.birthDate }}</td>
                    <td>{{ member.gender }}</td>
                    <td>{{ member.qualification }}</td>
                    <td>{{ splitDateRange(member.workPeriod).startDate }}</td>
                    <td>{{ splitDateRange(member.workPeriod).endDate }}</td>
                  </tr>
                </tbody>
              </v-table>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="closeDialog"
          >
            취소
          </v-btn>
          <v-btn
            color="#1e3a8a"
            variant="flat"
            :disabled="!memberFile"
            :loading="isUploadingMember"
            @click="uploadMemberFile"
          >
            업로드
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 변경 전/후 비교 모달 -->
    <v-dialog v-model="showComparisonDialog" max-width="1200px" content-class="comparison-dialog-wrapper">
      <v-card class="comparison-card">
        <v-card-title class="comparison-header">
          <v-icon color="white" class="mr-2">mdi-compare-horizontal</v-icon>
          변경사항 비교
        </v-card-title>
        
        <v-card-text class="comparison-content">
          <div class="comparison-summary">
            <v-chip color="#1e3a8a" variant="tonal" class="mr-2">
              <v-icon start>mdi-plus-circle</v-icon>
              추가: {{ comparisonData.summary.added }}명
            </v-chip>
            <v-chip color="#1e3a8a" variant="tonal" class="mr-2">
              <v-icon start>mdi-pencil</v-icon>
              수정: {{ comparisonData.summary.modified }}명
            </v-chip>
            <v-chip color="#1e3a8a" variant="tonal" class="mr-2">
              <v-icon start>mdi-minus-circle</v-icon>
              삭제: {{ comparisonData.summary.deleted }}명
            </v-chip>
            <v-chip color="#1e3a8a" variant="tonal">
              <v-icon start>mdi-check-circle</v-icon>
              변경없음: {{ comparisonData.summary.unchanged }}명
            </v-chip>
          </div>
          
          <div class="smart-comparison-table">
            <v-table class="comparison-table" fixed-header density="compact">
              <thead>
                <tr>
                  <th class="status-column">상태</th>
                  <th>성명</th>
                  <th>생년월일</th>
                  <th>성별</th>
                  <th>보유자격증</th>
                  <th>근무기간</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="comparisonData.comparison.length === 0">
                  <td colspan="6" class="text-center text-grey">비교할 데이터가 없습니다</td>
                </tr>
                <tr 
                  v-for="(item, index) in comparisonData.comparison" 
                  :key="index"
                  :class="`row-${item.status}`"
                >
                  <td class="status-cell">
                    <v-chip 
                      :color="getStatusColor(item.status)" 
                      size="small" 
                      variant="flat"
                      class="status-chip"
                    >
                      <v-icon start size="small">{{ getStatusIcon(item.status) }}</v-icon>
                      {{ getStatusText(item.status) }}
                    </v-chip>
                  </td>
                  <td :class="{ 'changed-cell': item.changes?.name }">
                    {{ item.name }}
                    <div v-if="item.changes?.name" class="change-indicator">
                      <small class="old-value">{{ item.changes.name.old }}</small>
                      <v-icon size="x-small" class="mx-1">mdi-arrow-right</v-icon>
                      <small class="new-value">{{ item.changes.name.new }}</small>
                    </div>
                  </td>
                  <td :class="{ 'changed-cell': item.changes?.birthDate }">
                    {{ item.birthDate }}
                    <div v-if="item.changes?.birthDate" class="change-indicator">
                      <small class="old-value">{{ item.changes.birthDate.old }}</small>
                      <v-icon size="x-small" class="mx-1">mdi-arrow-right</v-icon>
                      <small class="new-value">{{ item.changes.birthDate.new }}</small>
                    </div>
                  </td>
                  <td :class="{ 'changed-cell': item.changes?.gender }">
                    {{ item.gender }}
                    <div v-if="item.changes?.gender" class="change-indicator">
                      <small class="old-value">{{ item.changes.gender.old }}</small>
                      <v-icon size="x-small" class="mx-1">mdi-arrow-right</v-icon>
                      <small class="new-value">{{ item.changes.gender.new }}</small>
                    </div>
                  </td>
                  <td :class="{ 'changed-cell': item.changes?.qualification }">
                    {{ item.qualification }}
                    <div v-if="item.changes?.qualification" class="change-indicator">
                      <small class="old-value">{{ item.changes.qualification.old }}</small>
                      <v-icon size="x-small" class="mx-1">mdi-arrow-right</v-icon>
                      <small class="new-value">{{ item.changes.qualification.new }}</small>
                    </div>
                  </td>
                  <td :class="{ 'changed-cell': item.changes?.workPeriod }">
                    {{ item.workPeriod }}
                    <div v-if="item.changes?.workPeriod" class="change-indicator">
                      <small class="old-value">{{ item.changes.workPeriod.old }}</small>
                      <v-icon size="x-small" class="mx-1">mdi-arrow-right</v-icon>
                      <small class="new-value">{{ item.changes.workPeriod.new }}</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="cancelUpload"
          >
            취소
          </v-btn>
          <v-btn
            color="#1e3a8a"
            variant="flat"
            :loading="isUploadingMember"
            :disabled="!memberFile"
            @click="confirmUpload"
          >
            업로드 실행
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'uploaded'): void;
}>();

const store = useStore();

// 상태 관리
const isUploadingMember = ref(false);
const loadingText = ref('업로드 중...');
const memberFile = ref<File | null>(null);
const memberTableData = ref<any[]>([]);
const filteredMemberData = ref<any[]>([]);
const memberSearchFilter = ref('');
const excludeResigned = ref(false);
const memberSortState = ref({
  column: '',
  direction: 'asc' as 'asc' | 'desc'
});

// 비교 모달
const showComparisonDialog = ref(false);
const comparisonData = ref<{
  comparison: any[];
  summary: {
    total: number;
    added: number;
    deleted: number;
    modified: number;
    unchanged: number;
  };
}>({
  comparison: [],
  summary: {
    total: 0,
    added: 0,
    deleted: 0,
    modified: 0,
    unchanged: 0
  }
});

// 스낵바 표시
const showSnackbar = (text: string, color: string = 'success') => {
  const timeout = color === 'error' && text.length > 100 ? 8000 : 4000;
  store.commit('showSnackbar', { text, color, timeout });
};

// 요양보호사 데이터 조회
const fetchMemberData = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    console.warn('사용자 정보가 없습니다.');
    return;
  }

  try {
    const response = await axios.get(`/api/files/member-data/${user.m_sisul_code}`, {
      withCredentials: true
    });

    if (response.data.success) {
      memberTableData.value = response.data.data || [];
      filteredMemberData.value = [...memberTableData.value];
    } else {
      memberTableData.value = [];
      filteredMemberData.value = [];
    }
  } catch (error) {
    console.error('요양보호사 데이터 조회 오류:', error);
    memberTableData.value = [];
    filteredMemberData.value = [];
  }
};

// 파일 선택 핸들러
const onMemberFileSelect = async (files: File[]) => {
  const file = files[0];
  if (file) {
    memberFile.value = file;
    
    const user = store.state.user;
    if (!user || !user.m_sisul_code) {
      showSnackbar('사용자 정보를 찾을 수 없습니다.', 'error');
      return;
    }
    
    try {
      isUploadingMember.value = true;
      loadingText.value = '요양보호사 파일을 분석하는 중...';
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('institutionCode', user.m_sisul_code);
      
      const response = await axios.post('/api/files/member-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      if (response.data.success) {
        comparisonData.value = response.data.data;
        showComparisonDialog.value = true;
      } else {
        showSnackbar(response.data.message || '파일 분석에 실패했습니다.', 'error');
      }
    } catch (error: any) {
      console.error('파일 분석 오류:', error);
      if (error.response?.data?.message) {
        showSnackbar(error.response.data.message, 'error');
      } else {
        showSnackbar('파일 분석 중 오류가 발생했습니다.', 'error');
      }
    } finally {
      isUploadingMember.value = false;
    }
  }
};

// 업로드 확인
const confirmUpload = async () => {
  showComparisonDialog.value = false;
  await uploadMemberFile();
};

// 업로드 취소
const cancelUpload = () => {
  showComparisonDialog.value = false;
  memberFile.value = null;
  comparisonData.value = {
    comparison: [],
    summary: {
      total: 0,
      added: 0,
      deleted: 0,
      modified: 0,
      unchanged: 0
    }
  };
};

// 사원 정보 업로드
const uploadMemberFile = async () => {
  if (!memberFile.value) return;
  
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    showSnackbar('사용자 정보를 찾을 수 없습니다.', 'error');
    return;
  }
  
  try {
    isUploadingMember.value = true;
    loadingText.value = '요양보호사 정보를 업로드하는 중...';
    
    const formData = new FormData();
    formData.append('file', memberFile.value);
    formData.append('institutionCode', user.m_sisul_code);
    
    const response = await axios.post('/api/files/member-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true
    });
    
    if (response.data.success) {
      showSnackbar(response.data.message, 'success');
      await fetchMemberData();
      memberFile.value = null;
      emit('uploaded');
    } else {
      showSnackbar(response.data.message || '업로드에 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('사원 정보 업로드 오류:', error);
    if (error.response?.data?.message) {
      showSnackbar(error.response.data.message, 'error');
    } else {
      showSnackbar('업로드 중 오류가 발생했습니다.', 'error');
    }
  } finally {
    isUploadingMember.value = false;
  }
};

// 요양보호사 데이터 필터링
const filterMemberData = () => {
  let filtered = [...memberTableData.value];
  
  if (memberSearchFilter.value) {
    filtered = filtered.filter((member: any) => 
      member.name && 
      member.name.toString().toLowerCase().includes(memberSearchFilter.value.toLowerCase())
    );
  }
  
  if (excludeResigned.value) {
    filtered = filtered.filter((member: any) => {
      const endDate = splitDateRange(member.workPeriod).endDate;
      return !endDate || endDate === '' || endDate === '-';
    });
  }
  
  filteredMemberData.value = filtered;
};

// 요양보호사 데이터 정렬
const sortMemberData = (column: string) => {
  if (memberSortState.value.column === column) {
    memberSortState.value.direction = memberSortState.value.direction === 'asc' ? 'desc' : 'asc';
  } else {
    memberSortState.value.column = column;
    memberSortState.value.direction = 'asc';
  }
  
  filteredMemberData.value.sort((a: any, b: any) => {
    let aValue, bValue;
    
    if (column === 'workPeriod') {
      aValue = splitDateRange(a.workPeriod).startDate || '';
      bValue = splitDateRange(b.workPeriod).startDate || '';
    } else {
      aValue = a[column] || '';
      bValue = b[column] || '';
    }
    
    if (column === 'birthDate' || column === 'workPeriod') {
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return memberSortState.value.direction === 'asc' 
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }
    }
    
    if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
      return memberSortState.value.direction === 'asc' 
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    }
    
    const comparison = aValue.toString().localeCompare(bValue.toString());
    return memberSortState.value.direction === 'asc' ? comparison : -comparison;
  });
};

// 날짜 범위 분리
const splitDateRange = (dateRange: string) => {
  let [startDate, endDate] = dateRange.split("~").map(s => s.trim());
  if (endDate === '9999-12-31') {
    endDate = '';
  }
  return { startDate, endDate };
};

// 파일 크기 포맷팅
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 업로드 날짜 포맷팅
const formatUploadDate = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 상태별 색상 반환
const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    added: 'success',
    modified: 'warning', 
    deleted: 'error',
    unchanged: 'info'
  };
  return colors[status] || 'grey';
};

// 상태별 아이콘 반환
const getStatusIcon = (status: string): string => {
  const icons: Record<string, string> = {
    added: 'mdi-plus-circle',
    modified: 'mdi-pencil',
    deleted: 'mdi-minus-circle',
    unchanged: 'mdi-check-circle'
  };
  return icons[status] || 'mdi-help-circle';
};

// 상태별 텍스트 반환
const getStatusText = (status: string): string => {
  const texts: Record<string, string> = {
    added: '추가',
    modified: '수정',
    deleted: '삭제',
    unchanged: '변경없음'
  };
  return texts[status] || '알수없음';
};

// 다이얼로그 닫기
const closeDialog = () => {
  emit('update:modelValue', false);
  memberFile.value = null;
  memberSearchFilter.value = '';
  excludeResigned.value = false;
  memberSortState.value = { column: '', direction: 'asc' };
  filteredMemberData.value = [...memberTableData.value];
};

// 모달이 열릴 때 데이터 조회
watch(() => props.modelValue, async (newValue) => {
  if (newValue) {
    await fetchMemberData();
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/fonts.scss';

// v-dialog는 teleport되므로 전역 스타일 필요
.member-upload-dialog-wrapper,
.comparison-dialog-wrapper {
  font-family: $Noto_Sans_KR !important;
  
  * {
    font-family: $Noto_Sans_KR !important;
  }
}

// scoped 스타일로 나머지 적용
.member-upload-card {
  font-family: $Noto_Sans_KR !important;
  
  // Vuetify 컴포넌트들에 폰트 적용
  :deep(.v-card),
  :deep(.v-btn),
  :deep(.v-text-field),
  :deep(.v-select),
  :deep(.v-chip),
  :deep(.v-table),
  :deep(.v-file-input),
  :deep(.v-checkbox),
  :deep(.v-pagination) {
    font-family: $Noto_Sans_KR !important;
    
    * {
      font-family: $Noto_Sans_KR !important;
    }
  }

  .member-upload-header {
    background: #1e3a8a;
    color: white;
    padding: 12px 16px;
  }

  .member-upload-content {
    padding: 16px;
  }

  .member-table-container {
    .table-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }

      .file-upload-area {
        width: 300px;
      }
    }

    .file-preview {
      margin-bottom: 16px;
    }

    .member-search-filter {
      display: flex;
      gap: 16px;
      margin-bottom: 16px;
      align-items: center;
    }

    .table-wrapper {
      max-height: 400px;
      overflow-y: auto;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
    }
  }
}

.comparison-card {
  font-family: $Noto_Sans_KR !important;
  
  // Vuetify 컴포넌트들에 폰트 적용
  :deep(.v-card),
  :deep(.v-btn),
  :deep(.v-chip),
  :deep(.v-table) {
    font-family: $Noto_Sans_KR !important;
    
    * {
      font-family: $Noto_Sans_KR !important;
    }
  }

  .comparison-header {
    background: #1e3a8a;
    color: white;
    padding: 12px 16px;
  }

  .comparison-content {
    padding: 16px;
  }

  .comparison-summary {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .smart-comparison-table {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    max-height: 500px;
    overflow-y: auto;
  }

  .comparison-table {
    .status-column {
      width: 100px;
    }

    .status-cell {
      padding: 8px;
    }

    .status-chip {
      font-size: 0.75rem;
    }

    .changed-cell {
      background-color: #fff3cd;
    }

    .change-indicator {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 4px;
      font-size: 0.75rem;

      .old-value {
        color: #dc3545;
        text-decoration: line-through;
      }

      .new-value {
        color: #28a745;
        font-weight: 600;
      }
    }

    .row-added {
      background-color: #d4edda;
    }

    .row-modified {
      background-color: #fff3cd;
    }

    .row-deleted {
      background-color: #f8d7da;
    }

    .row-unchanged {
      background-color: #d1ecf1;
    }
  }
}

.member-upload-card {
  .member-table {
    th.sortable-header {
      cursor: pointer;
      user-select: none;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f5f5f5;
      }

      .header-content {
        display: flex;
        align-items: center;
        gap: 4px;

        .sort-icon {
          &.inactive {
            opacity: 0.3;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .member-upload-card {
    .member-table-container {
      .table-header {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;

        .file-upload-area {
          width: 100%;
        }
      }

      .member-search-filter {
        flex-direction: column;
        align-items: stretch;
      }
    }
  }
}
</style>

